import DOMPurify from "dompurify";
import { TrpcOutput } from "./trpc";

/**
 * Sanitize incoming HTML from Canvas, removing <script> & <link> tags while allowing iframes.
 * (DOMPurify already strips event handlers and javascript: URLs.)
 */
export function sanitizeCanvasHtml(html: string | null | undefined): string {
  if (!html) return "";
  return DOMPurify.sanitize(html, {
    FORBID_TAGS: ["script", "link"],
    ADD_TAGS: ["iframe"],
    ADD_ATTR: [
      "allow",
      "allowfullscreen",
      "frameborder",
      "scrolling",
      "target",
      "data-magic-link",
      "data-lqm-link",
      "data-chat-prompt",
      "data-chat-mode",
    ],
  });
}

/** Identifier for hosted brand variable CSS (can be overridden if needed). */
export const DEFAULT_BRAND_VARIABLES_ID = "cm028jfyu000050d8c2ojtawy";

/** Path to the canvas-isolated.css stylesheet, accounting for the dev vs. build base path. */
export function getCanvasIsolatedCssUrl(mode: string = import.meta.env.MODE): string {
  return mode === "development" ? "/canvas-isolated.css" : "/build/canvas-isolated.css";
}

/**
 * Compute the CSS links required for rendering isolated Canvas HTML.
 * Accepts optional overrides for brand variables id and current mode (for easier testing).
 */
export function getCanvasCssLinks(options?: { brandVariablesId?: string; mode?: string }): string[] {
  const { brandVariablesId = DEFAULT_BRAND_VARIABLES_ID, mode = import.meta.env.MODE } = options || {};
  return [
    "https://cdn.smartdesigner.visiarc.com/dist/preflight.css",
    "https://cdn.smartdesigner.visiarc.com/dist/output.css",
    `https://cdn.smartdesigner.visiarc.com/dist/${brandVariablesId}/variables.css`,
    getCanvasIsolatedCssUrl(mode),
  ];
}

/** Build a router link for a given module item, or null if unsupported. */
export function buildItemLink(
  item: NonNullable<TrpcOutput["course"]["getModules"]>[number]["subModules"][number]["items"][number],
): string | null {
  if (item.type === "Page" && item.page_url) return `/pages/${item.page_url}?module_item_id=${item.id}`;
  if (item.type === "Assignment" && item.quiz_lti === true && item.content_id)
    return `/quizzes/${item.content_id}?module_item_id=${item.id}`;
  return null;
}
